/**
 * CategoryPerformanceReport.js
 *
 * Two views toggled by tab:
 *  1. Heatmap — Students × Categories/Indicators grid (colour-coded by score)
 *  2. Leaderboard — Category-by-category ranking with vs-cohort comparison
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  FaFilter, FaSearch, FaSyncAlt, FaChartBar,
  FaThLarge, FaArrowUp, FaArrowDown,
  FaBirthdayCake, FaIdCard, FaTransgender,
  FaGraduationCap, FaBaseballBall, FaShieldAlt,
} from "react-icons/fa";
import { Colors } from "./branding";
import { useTheme, getT, glassCard } from "./ThemeContext";
import API_BASE from "./apiConfig";
const BASE = API_BASE;

const T = {
  blue:      Colors.primaryBlue || "#034e7b",
  dark:      "#02304f",
  yellow:    Colors.logoYellow  || "#f7ce0b",
  gray:      "#8899aa",
  lightGray: "#f0f4f8",
  font:      "'Segoe UI', system-ui, sans-serif",
  pageBg:    "linear-gradient(160deg,#e8f0f8 0%,#dce8f2 50%,#e4ecf5 100%)",
  glass:     "rgba(255,255,255,0.80)",
  glassBdr:  "1px solid rgba(255,255,255,0.60)",
  glassSh:   "0 4px 24px rgba(0,40,100,0.09)",
  topBar:    "linear-gradient(135deg,#02304f 0%,#034e7b 45%,#045a8d 75%,#0369a1 100%)",
};

// gc is now computed per-component via glassCard(T)

// Category colours (consistent with CoachPerformanceView)
const CAT_COLORS = ["#f59e0b","#3b82f6","#10b981","#8b5cf6","#ec4899","#14b8a6","#f97316","#6366f1"];
const catColor = idx => CAT_COLORS[idx % CAT_COLORS.length];

// ── Age category helpers (mirrors StudentMaster) ──────────────────────────────
const AGE_CAT_COLORS = {
  U9:    { bg: "#fef3c7", border: "#fbbf24", text: "#92400e" },
  U11:   { bg: "#d1fae5", border: "#34d399", text: "#065f46" },
  U13:   { bg: "#dbeafe", border: "#60a5fa", text: "#1e3a8a" },
  U15:   { bg: "#ede9fe", border: "#a78bfa", text: "#4c1d95" },
  U17:   { bg: "#fce7f3", border: "#f472b6", text: "#831843" },
  Adult: { bg: "#f1f5f9", border: "#94a3b8", text: "#334155" },
};

function computeAgeCategory(dobStr) {
  if (!dobStr) return null;
  const dob   = new Date(dobStr + "T00:00:00");
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  if (
    today.getMonth() < dob.getMonth() ||
    (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())
  ) age--;
  if (age < 9)  return "U9";
  if (age < 11) return "U11";
  if (age < 13) return "U13";
  if (age < 15) return "U15";
  if (age < 17) return "U17";
  return "Adult";
}

function AgeCatBadge({ code }) {
  if (!code) return null;
  const c = AGE_CAT_COLORS[code] || AGE_CAT_COLORS["Adult"];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "2px 9px", borderRadius: 20, fontSize: 10, fontWeight: 800,
      background: c.bg, border: `1.5px solid ${c.border}`, color: c.text,
      whiteSpace: "nowrap",
    }}>
      <FaBirthdayCake size={8} /> {code}
    </span>
  );
}

// ── Student mini-card — used in table rows (mirrors StudentMaster header style) ─
function StudentMiniCard({ s }) {
  const [imgErr, setImgErr] = useState(false);
  const { isDark } = useTheme();
  const DT = getT(isDark);
  const ageCode  = computeAgeCategory(s.date_of_birth);
  const initials = (s.student_name || "").split(" ").map(w => w[0]).slice(0,2).join("");

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
      {/* Avatar */}
      <div style={{
        width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
        overflow: "hidden",
        background: `linear-gradient(135deg, ${T.yellow}55, rgba(3,78,123,0.10))`,
        border: "2px solid rgba(255,200,0,0.35)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 13, fontWeight: 800, color: DT.textPrimary,
        boxShadow: "0 2px 8px rgba(0,40,100,0.10)",
      }}>
        {s.photo_url && !imgErr
          ? <img src={`${BASE}/${s.photo_url}`} alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={() => setImgErr(true)} />
          : <span>{initials}</span>
        }
      </div>
      {/* Info */}
      <div style={{ minWidth: 0 }}>
        {/* Row 1 — name + age badge */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 3 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: DT.textPrimary, fontFamily: T.font, whiteSpace: "nowrap" }}>
            {s.student_name}
          </span>
          {ageCode && <AgeCatBadge code={ageCode} />}
        </div>
        {/* Row 2 — meta chips */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 12px", fontSize: 10, color: DT.textMuted }}>
          {s.student_code && (
            <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
              <FaIdCard size={9} /> <strong style={{ color: DT.textSub }}>{s.student_code}</strong>
            </span>
          )}
          {s.gender && (
            <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
              <FaTransgender size={9} /> {s.gender}
            </span>
          )}
          {s.date_of_birth && (
            <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
              <FaBirthdayCake size={9} />
              {new Date(s.date_of_birth + "T00:00:00").toLocaleDateString("en-US")}
            </span>
          )}
          {(s.level_name || s.enrolled_level) && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 3,
              padding: "1px 7px", borderRadius: 10, fontSize: 10, fontWeight: 700,
              color: s.color_hex || T.yellow,
              background: (s.color_hex || T.yellow) + "18",
              border: `1px solid ${(s.color_hex || T.yellow)}44`,
            }}>
              <FaGraduationCap size={8} /> {s.level_name || s.enrolled_level}
            </span>
          )}
          {s.role_name && (
            <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
              <FaBaseballBall size={9} /> {s.role_name}
            </span>
          )}
          {s.cricclub_player_id && (
            <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
              <FaShieldAlt size={9} /> CricClub: <strong style={{ color: DT.textSub }}>{s.cricclub_player_id}</strong>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}


// Score → heat colour (0=white, 10=deep green, < avg = orange-red tint)
function heatColor(score, cohortAvg) {
  if (score === null || score === undefined) return { bg: "rgba(0,0,0,0.04)", text: "#ccc" };
  const pct = score / 10;
  if (pct >= 0.9) return { bg: "rgba(16,185,129,0.25)",  text: "#065f46" };
  if (pct >= 0.7) return { bg: "rgba(16,185,129,0.14)",  text: "#047857" };
  if (pct >= 0.5) return { bg: "rgba(59,130,246,0.13)",  text: "#1e40af" };
  if (pct >= 0.3) return { bg: "rgba(245,158,11,0.15)",  text: "#92400e" };
  return           { bg: "rgba(239,68,68,0.14)",          text: "#991b1b" };
}

function ScoreCell({ score, cohortAvg }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const { bg, text } = heatColor(score, cohortAvg);
  const diff = score !== null && score !== undefined ? score - cohortAvg : null;
  return (
    <td style={{
      padding: "8px 10px", textAlign: "center",
      background: bg, border: `1px solid ${T.divider}`,
      minWidth: 72,
    }}>
      {score !== null && score !== undefined ? (
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: text, fontFamily: T.font }}>{score.toFixed(1)}</div>
          {diff !== null && (
            <div style={{ fontSize: 9, fontWeight: 700, color: diff >= 0 ? "#059669" : "#dc2626", display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
              {diff >= 0 ? <FaArrowUp size={7}/> : <FaArrowDown size={7}/>}
              {Math.abs(diff).toFixed(1)}
            </div>
          )}
        </div>
      ) : (
        <span style={{ fontSize: 11, color: "#ccc" }}>—</span>
      )}
    </td>
  );
}

// ── Category summary card ─────────────────────────────────────────────────────
function CatSummaryCard({ cat, cidx, students }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const gc = glassCard(T);
  const color = catColor(cidx);
  // top scorer per category
  const ranked = [...students]
    .map(s => {
      const c = s.categories?.find(c => c.category_id === cat.category_id);
      return { name: s.student_name, code: s.student_code, avg: c?.cat_avg ?? 0 };
    })
    .sort((a, b) => b.avg - a.avg)
    .slice(0, 3);

  const cohortAvg = students.length
    ? (students.reduce((sum, s) => {
        const c = s.categories?.find(c => c.category_id === cat.category_id);
        return sum + (c?.cat_avg ?? 0);
      }, 0) / students.length)
    : 0;

  return (
    <div style={{ ...gc, padding: "18px 20px", borderTop: `3px solid ${color}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }}/>
        <span style={{ fontSize: 14, fontWeight: 800, color: T.textPrimary, fontFamily: T.font }}>{cat.category_name}</span>
        <span style={{ marginLeft: "auto", fontSize: 18, fontWeight: 900, color }}>{cohortAvg.toFixed(1)}</span>
      </div>

      {/* Category description */}
      {cat.description && (
        <p style={{ margin: "0 0 10px 18px", fontSize: 11, color: T.gray, fontFamily: T.font, lineHeight: 1.5 }}>
          {cat.description}
        </p>
      )}

      {/* Cohort avg bar */}
      <div style={{ height: 6, borderRadius: 3, background: "rgba(0,0,0,0.08)", overflow: "hidden", marginBottom: 12 }}>
        <div style={{ width: `${(cohortAvg / 10) * 100}%`, height: "100%", borderRadius: 3, background: color }}/>
      </div>

      {/* Indicators — name + description + cohort avg */}
      <div style={{ marginBottom: 12 }}>
        {cat.indicators?.map((ind) => (
          <div key={ind.indicator_id} style={{ marginBottom: 7 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0, opacity: 0.5 }}/>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.textPrimary, flex: 1, fontFamily: T.font }}>{ind.indicator_name}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color }}>{(ind.cohort_avg || 0).toFixed(1)}</span>
            </div>
            {ind.description && (
              <p style={{ margin: "2px 0 0 14px", fontSize: 10, color: T.gray, fontFamily: T.font, lineHeight: 1.4 }}>
                {ind.description}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Top 3 */}
      <div style={{ borderTop: "1px solid rgba(3,78,123,0.08)", paddingTop: 10 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: T.gray, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>Top Performers</div>
        {ranked.map((r, ri) => (
          <div key={ri} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <span style={{ fontSize: 10, fontWeight: 900, color: ri === 0 ? "#f59e0b" : T.gray, minWidth: 14 }}>
              {["🥇","🥈","🥉"][ri]}
            </span>
            <span style={{ fontSize: 12, color: T.textPrimary, fontFamily: T.font, flex: 1 }}>{r.name}</span>
            <span style={{ fontSize: 12, fontWeight: 800, color }}>{r.avg.toFixed(1)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Heatmap view ──────────────────────────────────────────────────────────────
function HeatmapView({ data }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const gc = glassCard(T);
  const { students, categories, indicators } = data;
  const [search, setSearch] = useState("");
  const [sortCat, setSortCat] = useState(null); // category_id to sort by

  const filtered = students.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return s.student_name.toLowerCase().includes(q) || s.student_code?.toLowerCase().includes(q);
  });

  const sorted = sortCat
    ? [...filtered].sort((a, b) => {
        const ca = a.categories?.find(c => c.category_id === sortCat)?.cat_avg ?? 0;
        const cb = b.categories?.find(c => c.category_id === sortCat)?.cat_avg ?? 0;
        return cb - ca;
      })
    : filtered;

  // All indicators flat list
  const flatInds = categories.flatMap((cat, cidx) =>
    (cat.indicators || []).map(iid => ({
      iid,
      cat_id: cat.category_id,
      cidx,
    }))
  );

  return (
    <div>
      {/* Search */}
      <div style={{ ...gc, padding: "12px 20px", marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
        <FaSearch size={11} color={T.gray}/>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search student…"
          style={{ border: "none", outline: "none", fontSize: 13, fontFamily: T.font, flex: 1, background: "transparent" }}/>
        <span style={{ fontSize: 12, color: T.gray }}>{sorted.length} students · Click a category header to sort</span>
      </div>

      {/* Heatmap table */}
      <div style={{ ...gc, overflow: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            {/* Category header row */}
            <tr style={{ background: "rgba(3,78,123,0.04)" }}>
              <th style={{ padding: "10px 14px", minWidth: 160, textAlign: "left", fontSize: 10, fontWeight: 800, color: T.gray, textTransform: "uppercase", letterSpacing: "0.07em", borderBottom: "2px solid rgba(3,78,123,0.08)" }}>
                Student
              </th>
              <th style={{ padding: "10px 10px", textAlign: "center", fontSize: 10, fontWeight: 800, color: T.gray, textTransform: "uppercase", letterSpacing: "0.07em", borderBottom: "2px solid rgba(3,78,123,0.08)", whiteSpace: "nowrap" }}>
                Overall
              </th>
              {categories.map((cat, cidx) => {
                const color = catColor(cidx);
                const indCount = (cat.indicators || []).length;
                return (
                  <th key={cat.category_id}
                    colSpan={indCount}
                    onClick={() => setSortCat(s => s === cat.category_id ? null : cat.category_id)}
                    style={{
                      padding: "10px 10px", textAlign: "center", cursor: "pointer", WebkitTapHighlightColor:"transparent",
                      fontSize: 11, fontWeight: 800, color,
                      borderBottom: `3px solid ${color}`,
                      borderLeft: `2px solid ${T.divider}`,
                      background: sortCat === cat.category_id ? `${color}12` : "transparent",
                      whiteSpace: "nowrap",
                    }}>
                    {cat.category_name}
                  </th>
                );
              })}
            </tr>
            {/* Indicator sub-header */}
            <tr style={{ background: "rgba(3,78,123,0.02)" }}>
              <th style={{ padding: "6px 14px", borderBottom: "1px solid rgba(3,78,123,0.08)" }}/>
              <th style={{ padding: "6px 10px", borderBottom: "1px solid rgba(3,78,123,0.08)", fontSize: 9, color: T.gray, textAlign: "center" }}>Avg</th>
              {categories.map((cat, cidx) => {
                const color = catColor(cidx);
                return (cat.indicators || []).map(iid => {
                  const ind = data.indicators?.find(i => i.indicator_id === iid);
                  return (
                    <th key={iid} style={{
                      padding: "6px 8px", fontSize: 9, fontWeight: 600, color: T.gray,
                      textAlign: "center", borderBottom: "1px solid rgba(3,78,123,0.08)",
                      borderLeft: `1px solid ${T.divider}`, minWidth: 72,
                      whiteSpace: "nowrap", maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {ind?.indicator_name?.split(" ").slice(0, 2).join(" ") || `Ind ${iid}`}
                    </th>
                  );
                });
              })}
            </tr>
          </thead>
          <tbody>
            {sorted.map((s, si) => {
              const bg = si % 2 === 0 ? T.rowEven : T.rowOdd;
              return (
                <tr key={s.student_id} style={{ background: bg }}>
                  {/* Student info */}
                  <td style={{ padding: "10px 14px", borderBottom: "1px solid rgba(3,78,123,0.05)", minWidth: 220 }}>
                    <StudentMiniCard s={s} />
                  </td>
                  {/* Overall avg */}
                  <ScoreCell score={s.overall_avg} cohortAvg={
                    students.length ? students.reduce((sum, st) => sum + st.overall_avg, 0) / students.length : 0
                  }/>
                  {/* Per-indicator scores */}
                  {categories.map((cat, cidx) => {
                    const catData = s.categories?.find(c => c.category_id === cat.category_id);
                    return (cat.indicators || []).map(iid => {
                      const ind = catData?.indicators?.find(i => i.indicator_id === iid);
                      const score = ind?.score ?? null;
                      const cohortAvg = ind?.cohort_avg ?? 0;
                      return <ScoreCell key={iid} score={score} cohortAvg={cohortAvg}/>;
                    });
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Heat legend */}
      <div style={{ ...gc, padding: "12px 20px", marginTop: 14, display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: T.gray, textTransform: "uppercase", letterSpacing: "0.07em" }}>Heat Scale</span>
        {[
          { label: "9–10  Elite",    bg: "rgba(16,185,129,0.25)" },
          { label: "7–8   Advanced", bg: "rgba(16,185,129,0.14)" },
          { label: "5–6   Mid",      bg: "rgba(59,130,246,0.13)" },
          { label: "3–4   Low",      bg: "rgba(245,158,11,0.15)" },
          { label: "0–2   Weak",     bg: "rgba(239,68,68,0.14)" },
          { label: "—     No data",  bg: "rgba(0,0,0,0.04)" },
        ].map(({ label, bg }) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 18, height: 18, borderRadius: 4, background: bg, border: "1px solid rgba(0,0,0,0.08)" }}/>
            <span style={{ fontSize: 11, color: T.gray }}>{label}</span>
          </div>
        ))}
        <span style={{ fontSize: 11, color: T.gray, marginLeft: "auto" }}>
          ▲▼ arrows = vs cohort average
        </span>
      </div>
    </div>
  );
}

// ── Leaderboard / category cards view ────────────────────────────────────────
function LeaderboardView({ data }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const gc = glassCard(T);
  const { students, cat_summary } = data;
  if (!students?.length) return <div style={{ padding: 40, textAlign: "center", color: T.gray }}>No data.</div>;

  return (
    <div>
      {/* Category summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 16, marginBottom: 24 }}>
        {(cat_summary || []).map((cat, cidx) => (
          <CatSummaryCard key={cat.category_id} cat={cat} cidx={cidx} students={students}/>
        ))}
      </div>

      {/* Full ranking table */}
      <div style={{ ...gc, overflow: "hidden" }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid rgba(3,78,123,0.08)", display: "flex", alignItems: "center", gap: 8 }}>
          <FaChartBar size={13} color={T.yellow}/>
          <span style={{ fontSize: 13, fontWeight: 800, color: T.textPrimary, fontFamily: T.font, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Student Rankings by Category
          </span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "rgba(3,78,123,0.04)" }}>
                <th style={{ padding: "10px 14px", fontSize: 10, fontWeight: 800, color: T.gray, textTransform: "uppercase", letterSpacing: "0.07em", textAlign: "left", borderBottom: "2px solid rgba(3,78,123,0.08)" }}>#</th>
                <th style={{ padding: "10px 14px", fontSize: 10, fontWeight: 800, color: T.gray, textTransform: "uppercase", letterSpacing: "0.07em", textAlign: "left", borderBottom: "2px solid rgba(3,78,123,0.08)" }}>Student</th>
                <th style={{ padding: "10px 14px", fontSize: 10, fontWeight: 800, color: T.gray, textTransform: "uppercase", letterSpacing: "0.07em", textAlign: "left", borderBottom: "2px solid rgba(3,78,123,0.08)" }}>Level</th>
                <th style={{ padding: "10px 14px", fontSize: 10, fontWeight: 800, color: T.gray, textTransform: "uppercase", letterSpacing: "0.07em", textAlign: "center", borderBottom: "2px solid rgba(3,78,123,0.08)" }}>Overall</th>
                {(cat_summary || []).map((cat, cidx) => (
                  <th key={cat.category_id} style={{
                    padding: "10px 14px", fontSize: 10, fontWeight: 800, color: catColor(cidx),
                    textTransform: "uppercase", letterSpacing: "0.07em", textAlign: "center",
                    borderBottom: `2px solid ${catColor(cidx)}55`, whiteSpace: "nowrap",
                  }}>{cat.category_name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {students.map((s, si) => (
                <tr key={s.student_id}
                  style={{ background: si % 2 === 0 ? T.rowEven : T.rowOdd, borderBottom: T.rowBdr }}
                  onMouseEnter={e => e.currentTarget.style.background = T.hoverRowBg}
                  onMouseLeave={e => e.currentTarget.style.background = si % 2 === 0 ? T.rowEven : T.rowOdd}
                >
                  <td style={{ padding: "10px 14px", fontSize: 11, color: T.gray, textAlign: "center" }}>{si + 1}</td>
                  <td style={{ padding: "10px 14px" }}>
                    <StudentMiniCard s={s} />
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <span style={{
                      padding: "2px 10px", borderRadius: 10, fontSize: 11, fontWeight: 700,
                      background: s.band_color + "22", color: s.band_color, border: `1px solid ${s.band_color}33`,
                    }}>{s.skill_band}</span>
                  </td>
                  <td style={{ padding: "10px 14px", textAlign: "center" }}>
                    <span style={{ fontSize: 15, fontWeight: 900, color: T.textPrimary }}>{s.overall_avg.toFixed(1)}</span>
                  </td>
                  {(cat_summary || []).map((cat, cidx) => {
                    const catData = s.categories?.find(c => c.category_id === cat.category_id);
                    const avg = catData?.cat_avg ?? null;
                    const vs  = catData?.vs_cohort ?? 0;
                    const color = catColor(cidx);
                    return (
                      <td key={cat.category_id} style={{ padding: "10px 14px", textAlign: "center" }}>
                        {avg !== null ? (
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 800, color }}>{avg.toFixed(1)}</div>
                            <div style={{ fontSize: 9, color: vs >= 0 ? "#059669" : "#dc2626", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                              {vs >= 0 ? <FaArrowUp size={7}/> : <FaArrowDown size={7}/>}
                              {Math.abs(vs).toFixed(1)} vs avg
                            </div>
                          </div>
                        ) : <span style={{ color: "#ccc", fontSize: 12 }}>—</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
export default function CategoryPerformanceReport() {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const gc = glassCard(T);
  const [data, setData]           = useState(null);
  const [batches, setBatches]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [filterBatch, setFilterBatch] = useState("");
  const [view, setView]           = useState("heatmap"); // "heatmap" | "leaderboard"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = filterBatch
        ? `${BASE}/reports/category-performance?batch_id=${filterBatch}`
        : `${BASE}/reports/category-performance`;
      const [dr, br] = await Promise.all([
        fetch(url).then(r => r.json()),
        fetch(`${BASE}/reports/batches`).then(r => r.json()),
      ]);
      setData(dr);
      setBatches(Array.isArray(br) ? br : []);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [filterBatch]);

  useEffect(() => { load(); }, [load]);

  const studentCount = data?.students?.length ?? 0;
  return (
    <div style={{ background: T.pageBg, minHeight: "100vh", fontFamily: T.font }}>

      {/* ── Page header ── */}
      <div style={{ display:"flex", alignItems:"center", gap:14, padding:"20px 24px 0", marginBottom:18, justifyContent:"space-between", flexWrap:"wrap" }}>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <div style={{ width:40, height:40, borderRadius:12, background:`linear-gradient(135deg,${T.yellow},#fff3a0)`, display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 3px 12px rgba(255,200,0,0.28)", flexShrink:0 }}>
            <FaChartBar size={16} color={T.blue}/>
          </div>
          <div>
            <h2 style={{ margin:0, fontSize:20, fontWeight:800, color:T.textPrimary, letterSpacing:"-0.01em" }}>Category Performance Report</h2>
            <p style={{ margin:"2px 0 0", fontSize:12, color:T.gray }}>
              Heatmap & leaderboard · per-indicator scores · vs cohort benchmark
            </p>
          </div>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          {/* View toggle */}
          <div style={{ display:"flex", background:`${T.yellow}18`, border:`1px solid ${T.yellow}44`, borderRadius:9, padding:3, gap:3 }}>
            {[
              { id: "heatmap",     label: "Heatmap",     Icon: FaThLarge },
              { id: "leaderboard", label: "Leaderboard", Icon: FaChartBar },
            ].map(({ id, label, Icon }) => (
              <button key={id} onClick={() => setView(id)} style={{
                display:"flex", alignItems:"center", gap:6, padding:"7px 14px",
                borderRadius:7, border:"none", cursor:"pointer", WebkitTapHighlightColor:"transparent", fontSize:12, fontWeight:700,
                background: view === id ? T.yellow : "transparent",
                color: view === id ? T.blue : T.gray,
              }}>
                <Icon size={11}/> {label}
              </button>
            ))}
          </div>
          <button onClick={load} disabled={loading} style={{
            display:"flex", alignItems:"center", gap:6, padding:"8px 14px",
            borderRadius:9, border:`1px solid ${T.yellow}55`, cursor:"pointer", WebkitTapHighlightColor:"transparent",
            background:`${T.yellow}22`, color:T.blue, fontSize:12, fontWeight:700,
          }}>
            <FaSyncAlt size={11} style={{ animation: loading ? "spin 1s linear infinite" : "none" }}/> Refresh
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "12px 24px 60px" }}>

        {/* ── Filters ── */}
        <div style={{ ...gc, padding: "12px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <FaFilter size={12} color={T.gray}/>
          <select value={filterBatch} onChange={e => setFilterBatch(e.target.value)} style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid rgba(3,78,123,0.15)", fontSize: 13, fontFamily: T.font , colorScheme: isDark ? "dark" : "light" }}>
            <option value="">All Batches</option>
            {batches.map(b => <option key={b.batch_id} value={b.batch_id}>{b.batch_name}</option>)}
          </select>
          <span style={{ fontSize: 12, color: T.gray, marginLeft: "auto" }}>
            {studentCount} student{studentCount !== 1 ? "s" : ""}
          </span>
        </div>

        {/* ── Content ── */}
        {loading ? (
          <div style={{ ...gc, padding: 80, textAlign: "center", color: T.gray, fontSize: 15 }}>Loading…</div>
        ) : !data ? (
          <div style={{ ...gc, padding: 80, textAlign: "center", color: T.gray, fontSize: 15 }}>No data available.</div>
        ) : view === "heatmap" ? (
          <HeatmapView data={data}/>
        ) : (
          <LeaderboardView data={data}/>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
